/**
 * OPT-09 后台反馈处理 API（23 号 §6.2 / 25 号 §4.3）。
 *
 * 对应后端：feedback.controller.ts + feedback.service.ts
 * - 列表与状态流转：SUPER_ADMIN / CUSTOMER_SERVICE；
 * - 联系方式仅返回脱敏值，`internalNote` 永不返回给用户端；
 * - RESOLVED→PROCESSING 重开必须带 reason。
 */
import type { ApiMeta } from '@liyu/shared-types';
import { apiRequest } from './client';
import type { Paginated } from './types';

export type FeedbackType = 'BUG' | 'CONTENT' | 'ACCOUNT' | 'OTHER';
export type FeedbackStatus = 'NEW' | 'PROCESSING' | 'RESOLVED';

export interface AdminFeedbackRow {
  id: string;
  userId: string;
  type: FeedbackType;
  content: string;
  status: FeedbackStatus;
  publicReply: string | null;
  internalNote: string | null;
  /** 服务端脱敏后的联系方式，无明文 */
  contactMasked: string | null;
  idempotencyKey: string | null;
  createdAt: string;
  handledAt: string | null;
}

export interface FeedbackListQuery {
  status?: FeedbackStatus;
  type?: FeedbackType;
  page?: number;
  pageSize?: number;
}

export interface FeedbackStatusInput {
  status: FeedbackStatus;
  /** 对用户公开的回复（可选，≤500） */
  publicReply?: string;
  /** 内部处理备注（≤1000，不返回给用户） */
  internalNote?: string;
  /** RESOLVED 重开 PROCESSING 必填 */
  reason?: string;
}

function toPaginated<T>(data: T[], meta?: ApiMeta, fallbackSize = 20): Paginated<T> {
  return {
    items: data,
    total: meta?.total ?? data.length,
    page: meta?.page ?? 1,
    pageSize: meta?.pageSize ?? fallbackSize,
  };
}

export async function listFeedback(
  query: FeedbackListQuery = {},
): Promise<Paginated<AdminFeedbackRow>> {
  const result = await apiRequest<AdminFeedbackRow[]>('/feedback', {
    query: {
      status: query.status,
      type: query.type,
      page: query.page,
      pageSize: query.pageSize,
    },
  });
  return toPaginated(result.data, result.meta, query.pageSize ?? 20);
}

export async function updateFeedbackStatus(
  id: string,
  body: FeedbackStatusInput,
): Promise<AdminFeedbackRow> {
  return (await apiRequest<AdminFeedbackRow>(`/feedback/${id}/status`, { method: 'PATCH', body }))
    .data;
}

/**
 * 查看完整联系方式（后端强制 reason，写入审计）。
 * 明文仅存在于本次响应，不落本地存储。
 */
export async function revealFeedbackContact(id: string, reason: string): Promise<string> {
  const result = await apiRequest<{ contact: string }>(`/feedback/${id}/reveal-contact`, {
    method: 'POST',
    body: { reason },
  });
  return result.data.contact;
}
